class PdpGallery {
  constructor() {
    this.gallery = document.querySelector('[data-pdp-gallery]');
    this.mobileSlider = null;
    this.desktopThumbSlider = null;
    this.mobileThumbSlider = null;
    this.currentVariantId = null;

    if (!this.gallery) return;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      this.initialize();
    }
  }

  initialize() {
    this.desktopThumbs = Array.from(this.gallery.querySelectorAll('[data-gallery-thumb]'));
    this.mobileThumbs = Array.from(this.gallery.querySelectorAll('[data-gallery-mobile-thumb]'));
    this.thumbs = [...this.desktopThumbs, ...this.mobileThumbs];
    this.mainItems = Array.from(this.gallery.querySelectorAll('[data-gallery-main]'));
    this.mainImages = Array.from(this.gallery.querySelectorAll('.pdp-gallery-main-image'));
    this.modal = document.getElementById('media-modal');
    this.modalSlides = this.modal ? Array.from(this.modal.querySelectorAll('.splide__slide')) : [];
    this.modalSplide = null;
    this.mobileElement = this.gallery.querySelector('[data-gallery-mobile]');
    this.mobileSlides = Array.from(this.gallery.querySelectorAll('.pdp-gallery-mobile-slide'));
    this.desktopThumbElement = this.gallery.querySelector('[data-gallery-thumb-slider]');
    this.mobileThumbElement = this.gallery.querySelector('[data-gallery-mobile-thumb-slider]');
    this.desktopMainPrev = this.gallery.querySelector('[data-gallery-main-prev]');
    this.desktopMainNext = this.gallery.querySelector('[data-gallery-main-next]');
    this.counter = this.gallery.querySelector('[data-gallery-counter]');
    this.swipeHint = this.gallery.querySelector('[data-gallery-swipe-hint]');
    this.hasDismissedSwipeHint = false;

    this.bindThumbs();
    this.bindMainImages();
    this.bindDesktopMainNav();
    this.bindModalControls();
    this.bindModalGlobals();
    this.initThumbSliders();
    this.initMobileSlider();
    this.observeVariantChanges();
    this.selectInitialVariantMedia();
    this.showGallery();
  }

  bindDesktopMainNav() {
    if (!this.desktopMainPrev && !this.desktopMainNext) return;

    this.desktopMainPrev?.addEventListener('click', (event) => {
      event.preventDefault();
      const activeIndex = this.getActiveMediaIndex();
      this.setActiveMedia(Math.max(activeIndex - 1, 0), { syncMobile: true });
    });

    this.desktopMainNext?.addEventListener('click', (event) => {
      event.preventDefault();
      const activeIndex = this.getActiveMediaIndex();
      const maxIndex = Math.max(this.mainItems.length - 1, 0);
      this.setActiveMedia(Math.min(activeIndex + 1, maxIndex), { syncMobile: true });
    });

    this.updateMainNavArrows(this.getActiveMediaIndex());
  }

  bindThumbs() {
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = Number(thumb.dataset.index || 0);
        this.setActiveMedia(index, { syncMobile: true });
      });
    });
  }

  bindMainImages() {
    // Remove the mainImages loop entirely — the mainItems listener covers it
    
    this.mainItems.forEach((item) => {
      item.addEventListener('click', (event) => {
        if (!item.querySelector('.pdp-gallery-main-image')) return;
        if (event.target && event.target.tagName === 'VIDEO') return;
        const index = Number(item.dataset.index || 0);
        this.openModal(index);
      });
    });
  
    this.mobileSlides.forEach((slide) => {
      slide.addEventListener('click', (event) => {
        if (!slide.querySelector('.pdp-gallery-mobile-image')) return;
        if (event.target && event.target.tagName === 'VIDEO') return;
        const index = Number(slide.dataset.index || 0);
        this.openModal(index);
      });
    });
  }

  bindModalGlobals() {
    if (!this.modal) return;

    window.openModal = (index = 0) => this.openModal(index);
    window.closeModal = () => this.closeModal();
  }

  bindModalControls() {
    if (!this.modal) return;

    this.modal.addEventListener('click', (event) => {
      if (event.target === this.modal) {
        this.closeModal();
      }
    });

    this.modal.querySelectorAll('.media-modal-close').forEach((button) => {
      button.addEventListener('click', () => this.closeModal());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modal.classList.contains('active')) {
        this.closeModal();
      }
    });
  }

  openModal(index = 0) {
    if (!this.modal) return;
  
    const startIndex = this.getSafeModalIndex(index);
  
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  
    const initSplide = () => {
      if (typeof Splide !== 'function') {
        this.modalSlides.forEach((slide) => slide.classList.remove('is-active'));
        this.modalSlides[startIndex]?.classList.add('is-active');
        return;
      }
  
      if (this.modalSplide) {
        this.modalSplide.destroy(true);
        this.modalSplide = null;
      }
  
      this.modalSplide = new Splide(this.modal, {
        type: 'slide',
        perPage: 1,
        perMove: 1,
        arrows: true,
        pagination: false,
        drag: true,
        speed: 360,
        start: startIndex,
      });
  
      this.modalSplide.on('mounted', () => {
        this.modalSplide.Components.Move.jump(startIndex);
      });
  
      this.modalSplide.mount();
    };
  
    // Wait for the scale transition to finish before mounting
    this.modal.addEventListener('transitionend', function handler(e) {
      // transitionend fires for each property (opacity, transform) — only run once
      if (e.propertyName !== 'transform') return;
      this.removeEventListener('transitionend', handler);
      initSplide();
    });
  }

  getSafeModalIndex(index) {
    const parsedIndex = Number(index);
    const maxIndex = Math.max(this.modalSlides.length - 1, 0);

    if (!Number.isInteger(parsedIndex)) return 0;

    return Math.min(Math.max(parsedIndex, 0), maxIndex);
  }

  setModalIndex(index) {
    if (!this.modalSplide) return;

    this.modalSplide.refresh();

    if (typeof this.modalSplide.Components?.Move?.jump === 'function') {
      this.modalSplide.Components.Move.jump(index);
      return;
    }

    this.modalSplide.go(index);
  }

  closeModal() {
    if (!this.modal) return;
  
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
  
    if (this.modalSplide) {
      this.modalSplide.destroy(true);
      this.modalSplide = null;
    }
  }

  initThumbSliders() {
    if (typeof Splide !== 'function') return;

    if (this.mobileThumbElement) {
      this.mobileThumbSlider = new Splide(this.mobileThumbElement, {
        type: 'slide',
        fixedWidth: '96px',
        fixedHeight: '96px',
        gap: '8px',
        perMove: 1,
        arrows: false,
        pagination: false,
        drag: true,
        speed: 360,
        trimSpace: true,
        omitEnd: true,
      }).mount();
    }
  }

  initMobileSlider() {
    if (!this.mobileElement || typeof Splide !== 'function') return;

    this.mobileSlider = new Splide(this.mobileElement, {
      type: 'slide',
      perPage: 1,
      perMove: 1,
      arrows: false,
      pagination: false,
      speed: 420,
      drag: true,
    });

    this.mobileSlider.on('mounted moved', () => {
      const index = this.mobileSlider.index || 0;
      this.setActiveMedia(index, { syncMobile: false });
      this.updateCounter(index);
      this.updateSwipeHint(index);
    });

    this.mobileSlider.on('drag', () => {
      this.dismissSwipeHint();
    });

    this.mobileSlider.mount();
    this.updateCounter(0);
    this.updateSwipeHint(0);
  }

  observeVariantChanges() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data?.variant?.id) {
          this.currentVariantId = data.variant.id.toString();
          this.selectMediaForCurrentVariant();
        }
      });
    }

    document.querySelectorAll('input[name="id"], select[name="id"]').forEach((selector) => {
      selector.addEventListener('change', () => {
        if (selector.value) {
          this.currentVariantId = selector.value.toString();
          this.selectMediaForCurrentVariant();
        }
      });
    });

    window.addEventListener('popstate', () => {
      this.currentVariantId = null;
      this.selectMediaForCurrentVariant();
    });
  }

  selectInitialVariantMedia() {
    this.currentVariantId = this.getCurrentVariantId();
    this.selectMediaForCurrentVariant();
  }

  selectMediaForCurrentVariant() {
    const color = this.getCurrentVariantColor();
    if (!color) return;

    const target = this.thumbs.find((thumb) => thumb.dataset.color === color);
    if (!target) return;

    this.setActiveMedia(Number(target.dataset.index || 0), { syncMobile: true });
  }

  setActiveMedia(index, { syncMobile } = { syncMobile: true }) {
    this.pauseInactiveVideos(index);

    this.thumbs.forEach((thumb) => {
      const isActive = Number(thumb.dataset.index || 0) === index;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.mainItems.forEach((item) => {
      item.classList.toggle('is-active', Number(item.dataset.index || 0) === index);
    });

    if (syncMobile && this.mobileSlider && this.mobileSlider.index !== index) {
      this.mobileSlider.go(index);
    }

    this.syncThumbSliders(index);
    this.updateCounter(index);
    this.updateMainNavArrows(index);
  }

  getActiveMediaIndex() {
    const activeItem = this.mainItems.find((item) => item.classList.contains('is-active'));

    if (!activeItem) {
      return 0;
    }

    return Number(activeItem.dataset.index || 0);
  }

  updateMainNavArrows(index) {
    const maxIndex = Math.max(this.mainItems.length - 1, 0);

    if (this.desktopMainPrev) {
      this.desktopMainPrev.disabled = index <= 0;
      this.desktopMainPrev.setAttribute('aria-disabled', index <= 0 ? 'true' : 'false');
    }

    if (this.desktopMainNext) {
      this.desktopMainNext.disabled = index >= maxIndex;
      this.desktopMainNext.setAttribute('aria-disabled', index >= maxIndex ? 'true' : 'false');
    }
  }

  getMediaIndexFromImage(image) {
    const mainItem = image.closest('[data-gallery-main]');
    if (mainItem) {
      return Number(mainItem.dataset.index || 0);
    }

    return 0;
  }

  syncThumbSliders(index) {
    if (this.mobileThumbSlider && this.mobileThumbSlider.index !== index) {
      this.mobileThumbSlider.go(index);
    }

    const activeDesktopThumb = this.desktopThumbs.find((thumb) => Number(thumb.dataset.index || 0) === index);
    if (activeDesktopThumb) {
      activeDesktopThumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  pauseInactiveVideos(activeIndex) {
    this.mainItems.forEach((item) => {
      if (Number(item.dataset.index || 0) === activeIndex) return;

      item.querySelectorAll('video').forEach((video) => {
        video.pause();
      });
    });
  }

  updateCounter(index) {
    if (!this.counter) return;

    const total = this.mobileSlides.length || this.mainItems.length;
    this.counter.textContent = `${index + 1} of ${total}`;
  }

  updateSwipeHint(index) {
    if (!this.swipeHint) return;

    this.swipeHint.classList.toggle('is-hidden', this.hasDismissedSwipeHint || index > 1);
  }

  dismissSwipeHint() {
    if (this.hasDismissedSwipeHint) return;

    this.hasDismissedSwipeHint = true;
    this.updateSwipeHint(this.mobileSlider?.index || 0);
  }

  getCurrentVariantId() {
    if (this.currentVariantId) return this.currentVariantId;

    const params = new URLSearchParams(window.location.search);
    return params.get('variant');
  }

  getCurrentVariantColor() {
    const variantId = this.getCurrentVariantId();
    if (!variantId || !window.productColorVariantMap) return null;

    return window.productColorVariantMap[variantId] || null;
  }

  showGallery() {
    requestAnimationFrame(() => {
      this.gallery.classList.add('fade-in');
    });
  }
}

new PdpGallery();
